import Diagram from './Diagram.jsx'
import TierBadge from './TierBadge.jsx'
import { useDocumentTitle } from '../useDocumentTitle.js'

// 🔴 THE PAGE TYPE, now actually shared. Title block → sticky skim rail → 68ch
// prose column → disclosures → figure frame → stat row.
//
// It lived inside routes/Explainer.jsx and its own header called it "A PAGE
// TYPE, NOT A PAGE" — which was true of the markup and not of the module, so
// the second page to want it (/docs) could only have it by copy-paste. Two
// copies of a page type is how a design system becomes three products wearing
// a hat, which is the exact failure docs/design-briefs/website.md §1 says the
// system exists to prevent. Extracted, not duplicated.
//
// 🔴 AUTHORED, NOT EXPORTED. Nothing rendered through here may name a weak
// spot, a single point of failure or an operational hazard. Read spec §4
// before adding a sentence to any content-src document that lands here:
// docs/d2d-architecture.md is an internal defect register, and paraphrasing it
// closely is still publishing it. site/publishing-guard.json fails the build on
// the specific hazard content — it is a backstop for a mistake, not permission.
//
// Layered, as asked: the narrative reads straight through with nothing
// expanded, and every technical detail sits behind a <details>.

function Stat({ label, value }) {
  return (
    <div className="d2d-stat">
      <p className="d2d-stat__label d2d-micro">{label}</p>
      {/* Data never waits on motion and there are no count-up animations: the
          value is in the DOM at first paint. A null value is an honest gap,
          not a zero. */}
      <p
        className={`d2d-stat__value${value === null ? ' d2d-stat__value--unknown' : ''}`}
        data-testid={`stat-${label}`}
      >
        {value === null ? 'Not published yet' : value}
      </p>
    </div>
  )
}

function Section({ section, figureCaption }) {
  return (
    <section id={section.id} aria-labelledby={`${section.id}-heading`}>
      <h2 id={`${section.id}-heading`}>{section.title}</h2>
      {section.paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}

      {section.figure ? <Diagram caption={figureCaption} /> : null}

      {section.timeline ? (
        <dl>
          {section.timeline.map(([day, title, body]) => (
            <div key={day}>
              <dt className="d2d-micro d2d-eyebrow">{day}</dt>
              <dd>
                <strong>{title}</strong> {body}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {section.items ? (
        <dl>
          {section.items.map(([term, description]) => (
            <div key={term}>
              <dt>
                <strong>{term}</strong>
              </dt>
              <dd>{description}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* Disclosure LAST, after the substance it elaborates on. It used to sit
          directly under the paragraphs, which no explainer section noticed
          because none of them pairs a disclosure with a timeline. /docs does:
          "The wheel, and why it is mostly gone" rendered ABOVE the Sun–Fri
          timeline it is a footnote to. Found by reading the built page. */}
      {section.disclosure ? (
        <details className="d2d-disclosure">
          <summary>
            {section.disclosure.summary}
            <span className="d2d-disclosure__plus" aria-hidden="true">
              +
            </span>
          </summary>
          <div className="d2d-disclosure__panel">
            <dl>
              {section.disclosure.items.map(([term, description]) => (
                <div key={term}>
                  <dt>
                    <code>{term}</code>
                  </dt>
                  <dd>{description}</dd>
                </div>
              ))}
            </dl>
            {section.disclosure.note ? <p>{section.disclosure.note}</p> : null}
          </div>
        </details>
      ) : null}

      {section.stats ? (
        <div className="d2d-stats">
          {section.stats.map((stat) => (
            <Stat key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </div>
      ) : null}
    </section>
  )
}

// `directory` — P6-104. An optional block between the title and the prose, for
// the one page that is a front door as well as a document: /docs lists the
// feature pages beneath it there. It is a NODE rather than data because what
// goes in it is a set of links to routes, and DocPage has no business knowing
// about routing — see routes/DocsIndex.jsx, which owns it.
//
// Optional, so the four pages that are only documents pass nothing and render
// exactly as they did. That is the reason it is a slot on the existing page type
// rather than a second page type: two page types is how a design system becomes
// three products wearing a hat, which is what extracting this file prevented.
export default function DocPage({ doc, directory = null }) {
  useDocumentTitle(doc.title)

  return (
    <article className="d2d-page">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">{doc.eyebrow}</p>
        <h1 className="d2d-titleblock__headline">{doc.title}</h1>
        <p className="d2d-titleblock__lede">{doc.intro}</p>
        <hr className="d2d-rule" />
        <p className="d2d-titleblock__meta d2d-micro">
          {doc.meta} · <TierBadge tier={1} />
        </p>
      </header>

      {directory}

      <div className="d2d-doc">
        <nav className="d2d-skimrail" aria-label="On this page">
          <p className="d2d-footer__head d2d-micro">On this page</p>
          <ul>
            {doc.sections.map((section) => (
              <li key={section.id}>
                <a className="d2d-skimrail__link" href={`#${section.id}`}>
                  {section.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="d2d-prose">
          {doc.sections.map((section) => (
            <Section key={section.id} section={section} figureCaption={doc.diagram?.caption} />
          ))}
        </div>
      </div>
    </article>
  )
}
